import React, { useState, useContext, useEffect } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { Link } from "react-router-dom";
import { Button, Form } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import logo from '../../assets/images/logo/COLLEGELOGO.jpeg';
import Preloader from '../../components/Preloader/Preloader';
import PasswordStrengthIndicator from "../../components/passwordStrengthIndicator";
import { Helmet } from 'react-helmet-async';

const isNumberRegx = /\d/;
const specialCharacterRegx = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
const capsRegx = /[A-Z ]+/;

export default function LoginPage() {
    const { validateLogin, setSidebarStatus } = useContext(ProductContext);
    const { enqueueSnackbar } = useSnackbar();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [loaded, setLoaded] = useState(true);
    const [loading, setLoading] = useState(false);

    const [passwordValidity, setPasswordValidity] = useState({
      minChar: null,
      number: null,
      specialChar: null,
      capsChar: null
    });
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [newpasswordFocused, setNewPasswordFocused] = useState(false);
    const [newpasswordValidity, setNewPasswordValidity] = useState({
      minChar: null,
      number: null,
      specialChar: null,
      capsChar: null
    });

    useEffect(() => {
        validateLogin();
        setSidebarStatus("Full");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    const handleLogin = async (event) => {
        event.preventDefault();
        try {
            if (!username.trim() || !password.trim()) {
              enqueueSnackbar("Please enter both Email and Password", { variant: 'info' });
              return;
            }
            setLoading(true);
            setLoaded(false);

            await signIn({ 
              username: username.trim(), 
              password: password 
            }).then(async (user) => {
              if (!user.isSignedIn && user.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                setPasswordFocused(false);
                setShowNewPass(true);
                enqueueSnackbar("Please set up your new permanent password", { variant: 'info' });
              } else {  
                await validateLogin();
              }
              setLoaded(true);
              setLoading(false);
            }).catch((error) => {
              setLoaded(true);
              setLoading(false);
              enqueueSnackbar("Authentication failed: " + error.message, { variant: 'warning' });
            });
        } catch (error) {
            setLoaded(true);
            setLoading(false);
            enqueueSnackbar("Error: " + error.message, { variant: 'warning' });            
        }
    };

    const handleChangePassword = async (event) => {
      event.preventDefault();
      try {
        setLoading(true);
        setLoaded(false);

        await confirmSignIn({
          challengeResponse: newPassword,
        }).then(async (user) => {
          if (user.isSignedIn) {
            await validateLogin();
            enqueueSnackbar("Password updated & authenticated successfully!", { variant: 'success' });
            setLoaded(true);    
          }
          setSidebarStatus("Orginal");
          setLoading(false);
        }).catch(e => {
          setLoaded(true);
          setLoading(false);
          enqueueSnackbar("Error: " + e.message, { variant: 'warning' });
        });
      } catch (error) {
        setLoaded(true);
        setLoading(false);
        enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
      }
    };

    const onChangePassword = (pass) => {
        setPassword(pass);
        setPasswordValidity({
            minChar: pass.length >= 8,
            number: isNumberRegx.test(pass),
            specialChar: specialCharacterRegx.test(pass),
            capsChar: capsRegx.test(pass)
        });
    };

    const onNewChangePassword = (pass) => {
      setNewPassword(pass);
      setNewPasswordValidity({
          minChar: pass.length >= 8,
          number: isNumberRegx.test(pass),
          specialChar: specialCharacterRegx.test(pass),
          capsChar: capsRegx.test(pass)
      });
    };

    return (
      <>
        <Helmet>
          <title>Student Portal Login - Vidyajyoti College of Theology Delhi</title>
          <meta name="description" content="Secure Student Login Portal for Vidyajyoti College of Theology" />
          <link rel="canonical" href="/login" />
        </Helmet>
       
        <Preloader show={!loaded} />

        <div className="login-page-wrapper">
          {/* Ambient Glows */}
          <div 
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '320px',
              height: '320px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.25) 0%, rgba(245, 158, 11, 0) 70%)',
              pointerEvents: 'none'
            }}
          />
          <div 
            style={{
              position: 'absolute',
              bottom: '-100px',
              left: '-80px',
              width: '380px',
              height: '380px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(37, 99, 235, 0.3) 0%, rgba(37, 99, 235, 0) 70%)',
              pointerEvents: 'none'
            }}
          />

          <div className="card auth-card animate-scale-in">
            {/* College Crest & Header */}
            <div className="text-center mb-4">
              <div className="d-inline-block position-relative mb-2">
                <img
                  src={logo}
                  alt="Vidyajyoti Crest"
                  width="82"
                  height="82"
                  className="rounded-circle shadow-sm"
                  style={{ border: '3px solid #1e3a8a', objectFit: 'cover' }}
                />
              </div>

              <h3 className="fw-bold mb-1" style={{ color: '#1e3a8a', fontFamily: 'Outfit, sans-serif' }}>
                Vidyajyoti College
              </h3>
              <div className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: '0.04em' }}>
                Faculty of Theology • Delhi
              </div>
              <div className="badge bg-primary px-3 py-1 mt-2 text-uppercase fw-bold" style={{ fontSize: '0.72rem', letterSpacing: '0.05em' }}>
                Student Portal Login
              </div>
            </div>

            {/* Login Form */}
            <Form onSubmit={showNewPass ? handleChangePassword : handleLogin}>
              <Form.Group className="mb-3" controlId="loginEmail">
                <Form.Label className="form-label small">
                  <i className="fa fa-envelope-o me-1 text-primary"></i> Registered Email / Username
                </Form.Label>
                <div className="auth-input-group">
                  <i className="fa fa-user-circle-o auth-icon-left"></i>
                  <Form.Control 
                    type="email" 
                    placeholder="student@vidyajyoticollege.in"
                    autoFocus={!showNewPass}
                    disabled={showNewPass || loading}
                    onChange={e => setUsername(e.target.value)}
                    value={username}
                    required
                    className="auth-input"
                  />
                </div>
              </Form.Group>

              {!showNewPass ? (
                <Form.Group className="mb-3" controlId="loginPassword">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="form-label small mb-0">
                      <i className="fa fa-lock me-1 text-primary"></i> Password
                    </Form.Label>
                    <Link to="/forgot-password" className="small text-primary fw-bold text-decoration-none">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="auth-input-group">
                    <i className="fa fa-key auth-icon-left"></i>
                    <Form.Control 
                      type={showPassword ? "text" : "password"} 
                      placeholder="Enter your account password"
                      onFocus={() => setPasswordFocused(true)}
                      onChange={e => onChangePassword(e.target.value)}            
                      value={password}
                      disabled={loading}
                      required
                      className="auth-input"
                    />
                    <button
                      type="button"
                      className="auth-btn-right"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                      tabIndex="-1"
                    >
                      <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3" controlId="loginNewPassword">
                  <Form.Label className="form-label small">
                    <i className="fa fa-shield me-1 text-warning"></i> Set New Password
                  </Form.Label>
                  <div className="auth-input-group">
                    <i className="fa fa-key auth-icon-left"></i>
                    <Form.Control 
                      type="password" 
                      placeholder="Enter new permanent password"
                      onFocus={() => setNewPasswordFocused(true)}
                      onChange={e => onNewChangePassword(e.target.value)}            
                      value={newPassword}
                      disabled={loading}
                      required
                      className="auth-input"
                    />
                  </div>
                </Form.Group>
              )}

              {passwordFocused && !showNewPass && (
                <div className="p-3 mb-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <PasswordStrengthIndicator validity={passwordValidity} />
                </div>
              )}

              {newpasswordFocused && showNewPass && (
                <div className="p-3 mb-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <PasswordStrengthIndicator validity={newpasswordValidity} />
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="btn-primary w-100 mt-3 py-2 fw-bold shadow-sm"
                disabled={loading}
                style={{ height: '48px', fontSize: '1rem', borderRadius: '12px' }}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i> Authenticating...
                  </>
                ) : !showNewPass ? (
                  <>
                    <i className="fa fa-sign-in me-2"></i> Sign In to Portal
                  </>
                ) : (
                  <>
                    <i className="fa fa-check-circle me-2"></i> Confirm New Password
                  </>
                )}
              </Button>
            </Form>

            {/* Footer Support */}
            <div className="text-center pt-3 border-top mt-3">
              <div className="text-muted small">
                Need student portal assistance?{' '}
                <a href="mailto:vjtheology@gmail.com" className="text-primary fw-bold text-decoration-none">
                  Contact Office
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    );
}
